import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import settings from 'settings';
import {
  convertToSelect, getKey, getLabel, setLowerDateRange, setUpperDateRange
} from 'library/helpers';
import _ from 'lodash';
import {
  Checkbox, Col, DatePicker, Divider, Form, Input, Row
} from 'antd';
import ExtendedSelect from 'components/extendedSelect/extended-select';
import DurationPicker from 'components/durationPicker/duration-picker';
import TagSelector from 'components/tagSelector/tag-selector';
import { FormWrapper } from 'components/Page';
import REDUCER_KEY from 'reducers/reducer.keys';
import { list } from 'actions';

const FormItem = Form.Item;

const mapStateToProps = state => ({
  leaveTypes: state.api.availabilityLeaveTypes.data.results,
  leaveTypesInFlight: state.api.availabilityLeaveTypes.inFlight,
  leaveStatus: state.api.availabilityLeaveStatuses.data.results,
  leaveStatusInFlight: state.api.availabilityLeaveStatuses.inFlight
});

const mapDispatchToProps = dispatch => ({
  loadTypes(searchText) {
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [];

    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }
    filters.push({
      id: 'active',
      value: true
    });
    filters.push({
      id: 'is_integration_managed',
      value: false
    });
    return dispatch(list(REDUCER_KEY.availabilityLeaveTypes.key, {
      page,
      pageSize,
      filters,
      sorted
    }));
  },
  loadStatus(searchText) {
    const page = 0;
    const pageSize = settings.APP_CONFIG.dropListSize;
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [];

    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }
    filters.push({
      id: 'active',
      value: true
    });
    filters.push({
      id: 'is_integration_managed',
      value: false
    });
    return dispatch(list(REDUCER_KEY.availabilityLeaveStatuses.key, {
      page,
      pageSize,
      filters,
      sorted
    }));
  }
});

const mapPropsToFields = (props) => {
  const { dataSource } = props;
  const {
    duration, type, status, start_datetime, end_datetime, itemTags
  } = dataSource;
  return {
    type: Form.createFormField({ value: convertToSelect(type) }),
    status: Form.createFormField({ value: convertToSelect(status) }),
    duration: Form.createFormField({ value: duration }),
    start_datetime: Form.createFormField({ value: start_datetime }),
    end_datetime: Form.createFormField({ value: end_datetime }),
    itemTags: Form.createFormField({ value: itemTags })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const FormInput = ({
  canAdmin,
  dataSource,
  form,
  inputFormItemLayout,
  leaveTypes,
  leaveTypesInFlight,
  leaveStatus,
  leaveStatusInFlight,
  loadTypes,
  loadStatus,
  themeName
}) => {
  const { getFieldDecorator } = form;
  const {
    duration, type, status, start_datetime, end_datetime, is_integration_managed, sap_reference, sap_change_date, itemTags
  } = dataSource;
  const readonly = !canAdmin || is_integration_managed;
  return (
    <Form>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Leave Type">
            {getFieldDecorator('type', {
              initialValue: type,
              rules: [
                {
                  required: true,
                  message: 'Please select a Leave Type.'
                }
              ]
            })(
              <ExtendedSelect
                placeholder="Select Leave Type"
                fetching={leaveTypesInFlight}
                dataSource={leaveTypes}
                optionKey={getKey}
                optionLabel={getLabel}
                onSearch={loadTypes}
                onFocus={loadTypes}
                disabled={readonly}
              />
            )}
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Start Date">
            {getFieldDecorator('start_datetime', {
              initialValue: start_datetime,
              rules: [
                {
                  required: true,
                  message: 'Please select a Start Date & Time.'
                }
              ]
            })(
              <DatePicker
                showTime={{
                  defaultValue: moment('00:00:00', 'HH:mm:ss'),
                  format: 'HH:mm'
                }}
                format="DD-MM-YYYY HH:mm"
                disabledDate={startValue => setUpperDateRange(startValue, end_datetime)}
                disabled={readonly}
              />
            )}
          </FormItem>
        </Col>
      </Row>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Leave Status">
            {getFieldDecorator('status', {
              initialValue: status,
              rules: [
                {
                  required: true,
                  message: 'Please select a Leave Status.'
                }
              ]
            })(
              <ExtendedSelect
                placeholder="Select Leave Status"
                fetching={leaveStatusInFlight}
                dataSource={leaveStatus}
                optionKey={getKey}
                optionLabel={getLabel}
                onSearch={loadStatus}
                onFocus={loadStatus}
                onBlur={loadStatus}
                disabled={readonly}
              />
            )}
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="End Date">
            {getFieldDecorator('end_datetime', {
              initialValue: end_datetime,
              rules: [
                {
                  required: true,
                  message: 'Please select an End Date & Time.'
                }
              ]
            })(
              <DatePicker
                showTime={{
                  defaultValue: moment('23:59:59', 'HH:mm:ss'),
                  format: 'HH:mm'
                }}
                format="DD-MM-YYYY HH:mm"
                disabledDate={endValue => setLowerDateRange(endValue, start_datetime)}
                disabled={readonly}
              />
            )}
          </FormItem>
        </Col>
      </Row>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Duration">
            {getFieldDecorator('duration', {
              initialValue: duration
            })(
              <DurationPicker showMinutes={false} disabled={readonly} />
            )}
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Tags">
            {getFieldDecorator('itemTags', {
              initialValue: itemTags
            })(
              <TagSelector
                theme={themeName}
                placeholder="Select Tags"
                tagSelectorReducerKey={REDUCER_KEY.userleaveCollectionValues}
                maxTagCount={2}
                disabled={!canAdmin}
              />
            )}
          </FormItem>
        </Col>
      </Row>
      <Divider />
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem label="SAP Reference">
            {
              getFieldDecorator('sap_reference', {
                initialValue: sap_reference
              })(<Input disabled />)
            }
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem label="SAP Change Date">
            {getFieldDecorator('sap_change_date', {
              initialValue: sap_change_date
            })(
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD-MM-YYYY HH:mm"
                disabled
              />
            )}
          </FormItem>
        </Col>
      </Row>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem>
            {
              getFieldDecorator('is_integration_managed', {
                initialValue: is_integration_managed,
                valuePropName: 'checked'
              })(
                <Checkbox disabled>
                  Managed in 1SAP
                </Checkbox>
              )
            }
          </FormItem>
        </Col>
      </Row>
    </Form>
  );
};

FormInput.propTypes = {
  canAdmin: PropTypes.bool,
  checkboxFormItemLayout: PropTypes.object,
  dataSource: PropTypes.object,
  form: PropTypes.object,
  inputFormItemLayout: PropTypes.object,
  itemTags: PropTypes.array,
  leaveTypes: PropTypes.array,
  leaveTypesInFlight: PropTypes.bool,
  leaveStatus: PropTypes.array,
  leaveStatusInFlight: PropTypes.bool,
  loadTypes: PropTypes.func,
  loadStatus: PropTypes.func,
  itemTagsInFlight: PropTypes.bool,
  onFieldsChange: PropTypes.func,
  onValuesChange: PropTypes.func,
  readonly: PropTypes.bool,
  themeName: PropTypes.string
};

FormInput.defaultProps = {
  canAdmin: false,
  checkboxFormItemLayout: {},
  dataSource: {},
  form: {},
  inputFormItemLayout: {},
  itemTags: [],
  leaveStatus: [],
  leaveTypes: [],
  leaveTypesInFlight: false,
  leaveStatusInFlight: false,
  itemTagsInFlight: false,
  loadTypes() {
  },
  loadStatus() {
  },
  onFieldsChange() {
  },
  onValuesChange() {
  },
  readonly: false,
  themeName: ''
};

const ConnectedFormInput = connect(
  mapStateToProps,
  mapDispatchToProps
)(FormInput);

const LeaveFormInput = FormWrapper(Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange
})(ConnectedFormInput));

export default LeaveFormInput;
